import {memoize} from '@packages/utilities/memo';
import {
  getDescendants,
  isUserComponentInstWithContextualFilterProp,
} from '@packages/utilities/platform';
import {
  isDynamicBoundOrConditioned,
  isDynamoWrapper,
} from '@packages/utilities/elementTypeUtils';
import {
  getDynamoItemElement,
  isConnectedDynamoWrapper,
  isDynamoWrapperWithDynContextFilter,
  isDynConditionedWithDynContextCondition,
  hasDynamoUniversalBindings,
} from '@packages/systems/dynamo/utils/DynamoElementUtils';

import {getBoundaryDefinition} from '../boundary-creators';
import {type ExpressionElement} from '@packages/systems/wfdl-ast';
import type {
  TopLevelDynamicContentFlags,
  BoundaryCreatorDynamicContentFlags,
} from './types';

type SourceElementAnalysis = {
  topLevelDynamicContentFlags: TopLevelDynamicContentFlags;
  topLevelBoundaryCreators: ExpressionElement[];
};

export const analyzeSourceElement = memoize(function _analyzeSourceElement(
  sourceElement: ExpressionElement
): SourceElementAnalysis {
  let orphanBindingsOrConditions = false;
  let orphanContextualConditions = false;
  let orphanContextualFilterProps = false;
  const topLevelBoundaryCreators: Array<ExpressionElement> = [];

  function elementVisitor(element: ExpressionElement, atBoundary: boolean) {
    if (atBoundary) {
      topLevelBoundaryCreators.push(element);
    }

    orphanBindingsOrConditions =
      orphanBindingsOrConditions ||
      isDynamicBoundOrConditioned(element) ||
      hasDynamoUniversalBindings(element);

    orphanContextualConditions =
      orphanContextualConditions ||
      isDynConditionedWithDynContextCondition(element);

    orphanContextualFilterProps =
      orphanContextualFilterProps ||
      isUserComponentInstWithContextualFilterProp(element);
  }

  visitElementTreeUntilBoundary(sourceElement, elementVisitor);

  const topLevelDynamicContentFlags = {
    orphanBindingsOrConditions,
    orphanContextualConditions,
    orphanContextualFilterProps,
  } as const;
  return {topLevelDynamicContentFlags, topLevelBoundaryCreators};
});

export function analyzeBoundaryCreators(
  boundaryCreators: ExpressionElement[]
): BoundaryCreatorDynamicContentFlags {
  const connectedDynamoWrappers = boundaryCreators.filter(
    (element) => isDynamoWrapper(element) && isConnectedDynamoWrapper(element)
  );

  const collectionListsWithContextualFilters = connectedDynamoWrappers.some(
    isDynamoWrapperWithDynContextFilter
  );

  const collectionListsWithContextualConditions = connectedDynamoWrappers.some(
    (dynamoWrapper) => {
      const dynamoItem = getDynamoItemElement(dynamoWrapper);
      return Boolean(
        dynamoItem && containsElementsWithContextualConditions(dynamoItem)
      );
    }
  );

  return {
    collectionListsWithContextualFilters,
    collectionListsWithContextualConditions,
  };
}

function containsElementsWithContextualConditions(
  tree: ExpressionElement
): boolean {
  let found = false;

  function contextualConditionVisitor(element: ExpressionElement) {
    if (found) {
      // stop searching if we have found a contextual condition
      return true;
    }
    found = isDynConditionedWithDynContextCondition(element);
  }

  visitElementTreeUntilBoundary(tree, contextualConditionVisitor);

  return found;
}

type VisitorFunction = (
  element: ExpressionElement,
  atBoundaryCreator: boolean
) => undefined | void | boolean;

function visitElementTreeUntilBoundary(
  tree: ExpressionElement,
  visitor: VisitorFunction
) {
  visitElementTree(tree, createStoppingVisitor(visitor));
}

type ComponentBoundaryDef = NonNullable<
  ReturnType<typeof getBoundaryDefinition>
>;

function createStoppingVisitor(visitor: VisitorFunction) {
  const deferredBoundaries: Record<string, ComponentBoundaryDef> = {};

  return function stoppingVisitor(element: ExpressionElement) {
    const boundary = getBoundaryDefinition(element);
    const atBoundaryCreator = boundary !== null;

    if (boundary) {
      deferredBoundaries[boundary.boundaryElementId] = boundary;
    }

    const deferredBoundary = deferredBoundaries[element.id];

    const shouldStopBeforeVisit = Boolean(
      deferredBoundary && deferredBoundary.boundaryType === 'startAt'
    );

    const shouldStopAfterVisit = Boolean(
      deferredBoundary && deferredBoundary.boundaryType === 'startAfter'
    );

    const shouldStop =
      shouldStopBeforeVisit ||
      visitor(element, atBoundaryCreator) ||
      shouldStopAfterVisit;

    if (shouldStop) {
      return true;
    }
  };
}

function visitElementTree(
  tree: ExpressionElement,
  visitor: (arg1: ExpressionElement) => undefined | boolean
) {
  // return true from `visitor` to stop recursing
  if (!visitor(tree)) {
    const children = getDescendants(tree);

    if (children) {
      children.forEach((child) => {
        visitElementTree(child, visitor);
      });
    }
  }
}
